import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export default function PrivacyPolicy() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center gap-4 max-w-3xl mx-auto">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 hover:bg-secondary rounded-full transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-xl font-semibold">Privacy Policy</h1>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="px-6 py-8 max-w-3xl mx-auto">
                <div className="prose prose-slate prose-sm max-w-none">
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Privacy Policy – Voilaw</h1>
                    
                    <div className="text-sm text-slate-500 mb-6">
                        <p className="mb-1"><strong>Effective Date:</strong> January 20, 2026</p>
                        <p><strong>Last Updated:</strong> January 20, 2026</p>
                    </div>

                    <p className="text-slate-600 mb-4">
                        This Privacy Policy describes how Voilaw ("Voilaw," "we," "us," or "our") collects, uses, discloses, and protects information when you access or use Voilaw.app and related websites, applications, software, and services (collectively, the "Platform").
                    </p>
                    <p className="text-slate-600 mb-6">
                        This Privacy Policy is incorporated into our <Link to="/terms" className="text-[#1C6EE7] hover:underline">Terms of Use</Link> and applies to visitors, trial users, and customers, including organization accounts and authorized users.
                    </p>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">1. What We Collect</h2>
                        <p className="text-slate-600 mb-4">
                            We collect information in three main ways: (a) you provide it, (b) we collect it automatically when you use the Platform, and (c) we receive it from service providers or integrations you enable.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">A. Information You Provide</h3>
                        <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                            <li><em>Account Information:</em> name, email address, password (hashed), phone number (if provided), organization name, billing contact details, user role/permissions.</li>
                            <li><em>Customer Data (Content):</em> information you enter or upload to the Platform, such as matter names, time entries, billing descriptions, tasks, notes, documents, tags, and related metadata.</li>
                            <li><em>Communications:</em> support requests, survey responses, emails, chat messages, or other communications with us.</li>
                            <li><em>Payment and Subscription Information:</em> limited billing details such as billing address and subscription plan selection. Payment card data is typically handled directly by our payment processor (e.g., Stripe), not stored by Voilaw.</li>
                        </ul>

                        <h3 className="text-base font-medium text-slate-700 mb-2">B. Information Collected Automatically</h3>
                        <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                            <li><em>Usage Data:</em> pages viewed, features used, clickstream, session duration, error logs, and device events.</li>
                            <li><em>Device and Network Data:</em> IP address, browser type, device identifiers, operating system, and approximate location derived from IP address.</li>
                            <li><em>Cookies and Similar Technologies:</em> cookies, local storage, pixels, and similar tools to keep you signed in, remember preferences, and measure performance.</li>
                        </ul>

                        <h3 className="text-base font-medium text-slate-700 mb-2">C. Information From Third Parties</h3>
                        <ul className="list-disc pl-6 text-slate-600 space-y-2">
                            <li><em>Payment Processors:</em> transaction status and limited payment metadata from processors like Stripe (e.g., payment success/failure, charge ID).</li>
                            <li><em>Integrations:</em> if you connect third-party services, we may receive data as authorized by you and the integration's settings.</li>
                            <li><em>Security Providers:</em> signals used to prevent fraud and abuse.</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">2. How We Use Information</h2>
                        <p className="text-slate-600 mb-2">We use information to:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li>Provide, operate, maintain, and improve the Platform</li>
                            <li>Create and administer accounts, including organization accounts and authorized users</li>
                            <li>Process subscriptions, payments, renewals, refunds (if any), and invoices</li>
                            <li>Provide customer support and respond to requests</li>
                            <li>Send service-related communications (e.g., security alerts, billing notices, feature updates, outages)</li>
                            <li>Monitor and improve performance, reliability, and user experience</li>
                            <li>Protect the Platform, investigate misuse, and enforce our Terms</li>
                            <li>Comply with legal obligations and respond to lawful requests</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">3. Legal Bases (If Applicable)</h2>
                        <p className="text-slate-600 mb-2">Depending on your location, Voilaw processes personal information based on:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li><em>Contract</em> (to provide the Platform you requested)</li>
                            <li><em>Legitimate interests</em> (security, fraud prevention, improvements, analytics)</li>
                            <li><em>Consent</em> (where required, such as certain cookies or marketing messages)</li>
                            <li><em>Legal obligations</em> (tax, compliance, lawful requests)</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">4. How We Share Information</h2>
                        <p className="text-slate-600 mb-4">
                            We do not sell your personal information in the conventional sense. We share information only as described below:
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">A. Service Providers ("Subprocessors")</h3>
                        <p className="text-slate-600 mb-4">
                            We use third-party vendors to operate the Platform (e.g., hosting, analytics, customer support tools, error monitoring, SMS delivery, and payment processing). These providers may process personal information on our behalf under contractual confidentiality and security obligations.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">B. Payment Processing (Stripe)</h3>
                        <p className="text-slate-600 mb-4">
                            Payments are processed by payment processors such as Stripe. Your payment card information is generally provided directly to Stripe and handled under Stripe's terms and privacy practices. We receive limited transaction information necessary to administer your subscription (e.g., payment status and identifiers).
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">C. SMS and Communications Providers</h3>
                        <p className="text-slate-600 mb-4">
                            If you provide a phone number and opt in (or if permitted for service messages), we may use SMS providers (e.g., Twilio or similar) to send account-related messages.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">D. Organization Accounts</h3>
                        <p className="text-slate-600 mb-4">
                            If you use the Platform through an organization account, your organization's administrators may have access to your account information and Customer Data as permitted by the plan configuration and admin controls.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">E. Legal, Safety, and Enforcement</h3>
                        <p className="text-slate-600 mb-2">We may disclose information if we believe in good faith it is necessary to:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-4">
                            <li>comply with law, regulation, subpoena, or court order</li>
                            <li>protect rights, safety, and security of Voilaw, users, or the public</li>
                            <li>investigate fraud, security incidents, or Terms violations</li>
                            <li>enforce our agreements</li>
                        </ul>

                        <h3 className="text-base font-medium text-slate-700 mb-2">F. Business Transfers</h3>
                        <p className="text-slate-600">
                            If Voilaw is involved in a merger, acquisition, financing, or sale of assets, information may be transferred as part of that transaction, subject to standard confidentiality protections.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">5. Cookies, Analytics, and Preferences</h2>
                        <p className="text-slate-600 mb-2">We use cookies and similar technologies for:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li><em>Essential functions:</em> authentication, security, session management</li>
                            <li><em>Performance and analytics:</em> understanding feature usage and improving the Platform</li>
                            <li><em>Preferences:</em> remembering settings</li>
                        </ul>
                        <p className="text-slate-600">
                            You can control cookies through your browser settings. Blocking some cookies may impact functionality.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">6. Email, Notifications, and SMS Text Messages</h2>

                        <h3 className="text-base font-medium text-slate-700 mb-2">A. Service Messages</h3>
                        <p className="text-slate-600 mb-4">
                            We may send operational messages about your account, security, billing, and Platform changes. These are not marketing messages.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">B. Marketing Messages (If Offered)</h3>
                        <p className="text-slate-600 mb-4">
                            If we send marketing communications, we will provide an unsubscribe mechanism. If required by law, marketing texts or emails will be sent only with your consent.
                        </p>

                        <h3 className="text-base font-medium text-slate-700 mb-2">C. SMS Terms</h3>
                        <p className="text-slate-600 mb-2">If you opt in to SMS:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li>Message frequency may vary</li>
                            <li>Message and data rates may apply</li>
                            <li>You can opt out by replying STOP</li>
                            <li>You can request help by replying HELP</li>
                            <li>Consent to receive texts is not required to purchase a subscription unless the message is strictly necessary to deliver the service you requested</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">7. Data Retention</h2>
                        <p className="text-slate-600 mb-2">We retain personal information and Customer Data for as long as necessary to:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>provide the Platform</li>
                            <li>comply with legal and accounting obligations</li>
                            <li>resolve disputes and enforce agreements</li>
                            <li>maintain security and prevent abuse</li>
                        </ul>
                        <p className="text-slate-600">
                            When an account is closed, we may retain certain information for a limited period for backups, compliance, security, and legitimate business purposes. Where feasible, we will delete or de-identify information after retention is no longer necessary.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">8. Security</h2>
                        <p className="text-slate-600">
                            We use administrative, technical, and physical safeguards designed to protect information. However, no system is completely secure, and we cannot guarantee absolute security. You are responsible for using strong passwords and maintaining the confidentiality of your login credentials.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">9. Your Rights and Choices</h2>
                        <p className="text-slate-600 mb-2">Depending on where you live, you may have rights to:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-4">
                            <li>access and receive a copy of your personal information</li>
                            <li>correct inaccuracies</li>
                            <li>delete personal information (subject to exceptions)</li>
                            <li>object to or restrict certain processing</li>
                            <li>withdraw consent where processing is based on consent</li>
                            <li>opt out of certain uses (such as targeted advertising, if applicable)</li>
                        </ul>

                        <h3 className="text-base font-medium text-slate-700 mb-2">U.S. State Privacy Rights (e.g., California)</h3>
                        <p className="text-slate-600 mb-3">
                            If applicable, you may have rights to know, delete, correct, and opt out of certain sharing of personal information. Voilaw does not sell personal information in exchange for money. If we engage in targeted advertising or similar practices, we will provide appropriate opt-out mechanisms.
                        </p>
                        <p className="text-slate-600">
                            To submit a request, contact us using the information in Section 13.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">10. International Users and Cross-Border Transfers</h2>
                        <p className="text-slate-600">
                            Voilaw may process and store information in the United States and other countries where we or our service providers operate. These locations may have different data protection laws than your jurisdiction.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">11. Children's Privacy</h2>
                        <p className="text-slate-600">
                            The Platform is not intended for children under 13 (or under 16 where applicable). We do not knowingly collect personal information from children. If you believe a child has provided information, contact us and we will take appropriate steps to delete it.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">12. Third-Party Links and Integrations</h2>
                        <p className="text-slate-600">
                            The Platform may contain links to third-party websites or services. This Privacy Policy does not apply to third-party practices. Review the privacy policies of those third parties before using their services.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">13. Contact Us</h2>
                        <p className="text-slate-600 mb-2">
                            If you have questions about this Privacy Policy or want to exercise your rights, contact us at:
                        </p>
                        <div className="bg-slate-50 rounded-xl p-4 text-slate-600">
                            <p><strong>Email:</strong> <a href="mailto:admin@voilaw.app" className="text-[#1C6EE7] hover:underline">admin@voilaw.app</a></p>
                            <p className="mt-2"><strong>Mailing Address:</strong><br />13531 Will Clayton Pkwy, Ste 300, #177<br />Humble, TX 77346</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">14. Changes to This Privacy Policy</h2>
                        <p className="text-slate-600">
                            We may update this Privacy Policy from time to time. We will post the updated version with a revised "Last Updated" date. If changes are material, we may provide additional notice through the Platform or by email.
                        </p>
                    </section>

                    <div className="mt-12 pt-6 border-t border-slate-200">
                        <p className="text-xs text-slate-400 text-center mb-2">
                            Last updated: January 20, 2026
                        </p>
                        <p className="text-xs text-slate-400 text-center">
                            <Link to="/terms" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                                Terms of Use
                            </Link>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}
